import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

async function authedFetch(path: string, token: string | null, init?: RequestInit): Promise<unknown> {
  const headers = new Headers(init?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init?.body) headers.set('Content-Type', 'application/json');
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return res.json();
}

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const AuditEntrySchema = z.object({
  id: z.number(),
  occurred_at: z.string(),
  tenant_id: z.number().nullable(),
  tenant_code: z.string().nullable(),
  actor_user_id: z.number().nullable(),
  actor_email: z.string().nullable(),
  action: z.string(),
  entity_type: z.string().nullable(),
  entity_id: z.number().nullable(),
  payload: z.record(z.unknown()),
});
export type AuditEntry = z.infer<typeof AuditEntrySchema>;

export const AuditPageSchema = z.object({
  data: z.array(AuditEntrySchema),
  next_cursor: z.string().nullable(),
});
export type AuditPage = z.infer<typeof AuditPageSchema>;

// ─── Filters + query-string builder ─────────────────────────────────────────

export interface AuditFilters {
  from?: string; to?: string;
  tenant_ids?: number[]; actor_user_ids?: number[];
  actions?: string[]; entity_types?: string[];
  entity_id?: string; payload_search?: string;
  cursor?: string; limit?: number;
}

export function buildAuditQueryString(f: AuditFilters): string {
  const qs = new URLSearchParams();
  if (f.from) qs.set('from', f.from);
  if (f.to) qs.set('to', f.to);
  for (const id of f.tenant_ids ?? []) qs.append('tenant_ids[]', String(id));
  for (const id of f.actor_user_ids ?? []) qs.append('actor_user_ids[]', String(id));
  for (const a of f.actions ?? []) qs.append('actions[]', a);
  for (const e of f.entity_types ?? []) qs.append('entity_types[]', e);
  if (f.entity_id) qs.set('entity_id', f.entity_id);
  if (f.payload_search) qs.set('payload_search', f.payload_search);
  if (f.cursor) qs.set('cursor', f.cursor);
  if (f.limit) qs.set('limit', String(f.limit));
  return qs.toString();
}

// ─── Query hook ───────────────────────────────────────────────────────────────

export function useAuditLog(token: string | null, filters: AuditFilters & { tenant_scope_id?: number } = {}) {
  const effectiveFilters: AuditFilters = filters.tenant_scope_id
    ? { ...filters, tenant_ids: [filters.tenant_scope_id] }
    : filters;
  const qs = buildAuditQueryString(effectiveFilters);
  return useQuery({
    queryKey: ['platform', 'audit', effectiveFilters],
    queryFn: async () => AuditPageSchema.parse(await authedFetch(`/admin/platform/audit?${qs}`, token)),
    enabled: token !== null,
  });
}

// ─── CSV download helper ──────────────────────────────────────────────────────

export async function downloadAuditCsv(filters: AuditFilters, accessToken: string): Promise<void> {
  const qs = buildAuditQueryString({ ...filters });
  const res = await fetch(`/api/v1/admin/platform/audit?${qs}&format=csv`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`CSV export failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
